<template>
    <div class="position-relative">
        <textarea
            ref="textarea"
            class="d-block font--16 lh--20 p-12 w-100 rounded-sm text-dark"
            :style="textareaStyle"
            :class="[
                borderClass,
                $css($theme.formControlPrimary),
                textTopClass,
            ]"
            :placeholder="cookedPlaceholder"
            :readonly="readonly"
            :disabled="disabled"
            :value="value"
            :maxlength="maxlength"
            @input="input"
            @focus="focus"
            @blur="blur"
        />

        <div
            v-if="innerLabel"
            :class="labelClass"
            class="position-absolute top left bg-light my-1 mx-2 transition-ease-out transition-duration-25 text-gray-dark pointer-events-none d-inline-block text-truncate"
            style="
                transition-property: font-size, padding-top, padding-bottom;
                width: calc(100% - 4px);
            "
        >
            {{ innerLabel }}
        </div>

        <input-status :status="status" />
    </div>
</template>

<script>
import InputStatus from "~/components/inputs/InputStatus"

export default {
    props: {
        ...InputStatus.props,

        placeholder: String,
        minHeight: {
            type: Number,
            default: null,
        },

        resizable: {
            type: Boolean,
            default: true,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        readonly: {
            type: Boolean,
            default: false,
        },

        innerLabel: {
            type: String,
            default: "",
        },

        value: String,

        maxlength: {
            type: [String, Number],
            default: null,
        },
    },

    emits: ["update:value", "focus", "blur"],

    data() {
        return {
            focused: false,
        }
    },

    computed: {
        borderClass() {
            return this.status.code ? `border-${this.status.code}` : ""
        },

        cookedPlaceholder() {
            if (!this.innerLabel) return this.placeholder
            if (this.focused && !this.value) {
                return this.placeholder
            } else {
                return ""
            }
        },

        textareaStyle() {
            return {
                resize: this.resizable ? "vertical" : "none",
                height: "100%",
                maxHeight: "100%",
                minHeight: this.minHeight ? `${this.minHeight}px` : "auto",
            }
        },

        textTopClass() {
            return this.innerLabel ? "pt-24" : "pt-12"
        },

        labelClass() {
            return {
                "font--16 lh--20 p-12": !(this.focused || this.value),
                "font--12 lh--16 pt-8 pb-2 px-12": this.focused || this.value,
            }
        },
    },

    methods: {
        input(event) {
            if (this.disabled) return

            this.$emit("update:value", event.target.value)
        },

        focus() {
            this.focused = true
            this.$emit("focus")
        },

        blur() {
            this.focused = false
            this.$emit("blur")
        },
    },

    components: {
        InputStatus,
    },
}
</script>

<style lang="scss" scoped>
@import "~/scss/bootstrap-importable";

textarea {
    // reset - (mozilla Firefox!)
    border: 1px solid;
    outline: none;
    box-shadow: none;

    border-color: $gray-medium;

    &:hover {
        border-color: $gray-dark;
    }

    &:disabled {
        border-color: $gray-medium;
        background-color: $light;
        opacity: 0.5;
    }
}

textarea::placeholder {
    color: $gray-dark;
}
</style>
